import React, { useState, useEffect, useMemo } from 'react';
import { Search, Replace, Copy, Download, Shuffle, Eye, Settings, AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';

interface ReplaceMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface ReplaceOptions {
  caseSensitive: boolean;
  wholeWords: boolean;
  useRegex: boolean;
  globalReplace: boolean;
  multiline: boolean;
  preserveCase: boolean;
  trimResults: boolean;
}

interface ReplaceStats {
  totalMatches: number;
  replacements: number;
  linesModified: number;
  charactersChanged: number;
}

const FindReplaceComponent: React.FC = () => {
  const [input, setInput] = useState('');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('basic');
  const [result, setResult] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [options, setOptions] = useState<ReplaceOptions>({
    caseSensitive: false,
    wholeWords: false,
    useRegex: false,
    globalReplace: true,
    multiline: true,
    preserveCase: false,
    trimResults: false
  });

  const replaceMethods: ReplaceMethod[] = [
    {
      id: 'basic',
      name: 'Basic Replace',
      description: 'Simple text find and replace',
      icon: <Search className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 'regex',
      name: 'Regular Expression',
      description: 'Pattern-based regex replacement',
      icon: <Search className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 'caseInsensitive',
      name: 'Case Insensitive',
      description: 'Ignore case differences',
      icon: <Replace className="w-4 h-4" />,
      color: 'purple'
    },
    {
      id: 'wholeWord',
      name: 'Whole Words',
      description: 'Match complete words only',
      icon: <Search className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 'lineReplace',
      name: 'Line Replace',
      description: 'Replace entire lines',
      icon: <Replace className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      id: 'multiReplace',
      name: 'Multiple Replace',
      description: 'Replace multiple terms (comma separated)',
      icon: <Replace className="w-4 h-4" />,
      color: 'indigo'
    },
    {
      id: 'caseTransform',
      name: 'Case Transform',
      description: 'Change case of matched text',
      icon: <RotateCcw className="w-4 h-4" />,
      color: 'pink'
    },
    {
      id: 'numberReplace',
      name: 'Number Replace',
      description: 'Find and replace numbers',
      icon: <Search className="w-4 h-4" />,
      color: 'orange'
    },
    {
      id: 'urlReplace',
      name: 'URL Replace',
      description: 'Find and replace URLs',
      icon: <Replace className="w-4 h-4" />,
      color: 'teal'
    },
    {
      id: 'emailReplace',
      name: 'Email Replace',
      description: 'Find and replace email addresses',
      icon: <Replace className="w-4 h-4" />,
      color: 'cyan'
    },
    {
      id: 'dateReplace',
      name: 'Date Replace',
      description: 'Find and replace date patterns',
      icon: <Replace className="w-4 h-4" />,
      color: 'emerald'
    },
    {
      id: 'customPattern',
      name: 'Custom Pattern',
      description: 'Advanced pattern matching',
      icon: <Settings className="w-4 h-4" />,
      color: 'violet'
    }
  ];

  const performReplace = (text: string, find: string, replace: string, method: string, opts: ReplaceOptions): { result: string; stats: ReplaceStats } => {
    if (!text || !find) {
      return {
        result: text,
        stats: { totalMatches: 0, replacements: 0, linesModified: 0, charactersChanged: 0 }
      };
    }

    let processedText = text;
    let matchCount = 0;
    let replacementCount = 0;
    const originalLength = text.length;

    try {
      setErrorMessage('');

      switch (method) {
        case 'basic':
          const basicResult = performBasicReplace(processedText, find, replace, opts);
          processedText = basicResult.text;
          matchCount = basicResult.matches;
          break;

        case 'regex':
          const regexResult = performRegexReplace(processedText, find, replace, opts);
          processedText = regexResult.text;
          matchCount = regexResult.matches;
          break;

        case 'caseInsensitive':
          const caseResult = performCaseInsensitiveReplace(processedText, find, replace, opts);
          processedText = caseResult.text;
          matchCount = caseResult.matches;
          break;

        case 'wholeWord':
          const wordResult = performWholeWordReplace(processedText, find, replace, opts);
          processedText = wordResult.text;
          matchCount = wordResult.matches;
          break;

        case 'lineReplace':
          const lineResult = performLineReplace(processedText, find, replace, opts);
          processedText = lineResult.text;
          matchCount = lineResult.matches;
          break;

        case 'multiReplace':
          const multiResult = performMultipleReplace(processedText, find, replace, opts);
          processedText = multiResult.text;
          matchCount = multiResult.matches;
          break;

        case 'caseTransform':
          const transformResult = performCaseTransform(processedText, find, replace, opts);
          processedText = transformResult.text;
          matchCount = transformResult.matches;
          break;

        case 'numberReplace':
          const numberResult = performNumberReplace(processedText, find, replace, opts);
          processedText = numberResult.text;
          matchCount = numberResult.matches;
          break;

        case 'urlReplace':
          const urlResult = performUrlReplace(processedText, find, replace, opts);
          processedText = urlResult.text;
          matchCount = urlResult.matches;
          break;

        case 'emailReplace':
          const emailResult = performEmailReplace(processedText, find, replace, opts);
          processedText = emailResult.text;
          matchCount = emailResult.matches;
          break;

        case 'dateReplace':
          const dateResult = performDateReplace(processedText, find, replace, opts);
          processedText = dateResult.text;
          matchCount = dateResult.matches;
          break;

        case 'customPattern':
          const customResult = performCustomPattern(processedText, find, replace, opts);
          processedText = customResult.text;
          matchCount = customResult.matches;
          break;

        default:
          const defaultResult = performBasicReplace(processedText, find, replace, opts);
          processedText = defaultResult.text;
          matchCount = defaultResult.matches;
      }

      if (opts.trimResults) {
        processedText = processedText.split('\n').map(line => line.trim()).join('\n');
      }

      replacementCount = matchCount;
      const linesModified = text.split('\n').filter((line, index) => {
        const resultLines = processedText.split('\n');
        return line !== (resultLines[index] || '');
      }).length;

      const charactersChanged = Math.abs(originalLength - processedText.length);

      return {
        result: processedText,
        stats: {
          totalMatches: matchCount,
          replacements: replacementCount,
          linesModified,
          charactersChanged
        }
      };

    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred during replacement');
      return {
        result: text,
        stats: { totalMatches: 0, replacements: 0, linesModified: 0, charactersChanged: 0 }
      };
    }
  };

  const performBasicReplace = (text: string, find: string, replace: string, opts: ReplaceOptions) => {
    const flags = opts.globalReplace ? 'g' : '';
    const searchFlags = opts.caseSensitive ? flags : flags + 'i';
    const regex = new RegExp(escapeRegex(find), searchFlags);
    const matches = (text.match(regex) || []).length;
    const result = text.replace(regex, replace);
    return { text: result, matches };
  };

  const performRegexReplace = (text: string, pattern: string, replace: string, opts: ReplaceOptions) => {
    const flags = `${opts.globalReplace ? 'g' : ''}${opts.caseSensitive ? '' : 'i'}${opts.multiline ? 'm' : ''}`;
    const regex = new RegExp(pattern, flags);
    const matches = (text.match(regex) || []).length;
    const result = text.replace(regex, replace);
    return { text: result, matches };
  };

  const performCaseInsensitiveReplace = (text: string, find: string, replace: string, opts: ReplaceOptions) => {
    const flags = opts.globalReplace ? 'gi' : 'i';
    const regex = new RegExp(escapeRegex(find), flags);
    const matches = (text.match(regex) || []).length;
    const result = text.replace(regex, replace);
    return { text: result, matches };
  };

  const performWholeWordReplace = (text: string, find: string, replace: string, opts: ReplaceOptions) => {
    const flags = `${opts.globalReplace ? 'g' : ''}${opts.caseSensitive ? '' : 'i'}`;
    const regex = new RegExp(`\\b${escapeRegex(find)}\\b`, flags);
    const matches = (text.match(regex) || []).length;
    const result = text.replace(regex, replace);
    return { text: result, matches };
  };

  const performLineReplace = (text: string, find: string, replace: string, opts: ReplaceOptions) => {
    const lines = text.split('\n');
    let matches = 0;
    const flags = opts.caseSensitive ? '' : 'i';
    const regex = new RegExp(escapeRegex(find), flags);
    
    const result = lines.map(line => {
      if (regex.test(line)) {
        matches++;
        return replace;
      }
      return line;
    }).join('\n');

    return { text: result, matches };
  };

  const performMultipleReplace = (text: string, findTerms: string, replace: string, opts: ReplaceOptions) => {
    const terms = findTerms.split(',').map(term => term.trim()).filter(term => term);
    let result = text;
    let totalMatches = 0;

    terms.forEach(term => {
      const flags = `${opts.globalReplace ? 'g' : ''}${opts.caseSensitive ? '' : 'i'}`;
      const regex = new RegExp(escapeRegex(term), flags);
      const matches = (result.match(regex) || []).length;
      totalMatches += matches;
      result = result.replace(regex, replace);
    });

    return { text: result, matches: totalMatches };
  };

  const performCaseTransform = (text: string, find: string, transform: string, opts: ReplaceOptions) => {
    const flags = `${opts.globalReplace ? 'g' : ''}${opts.caseSensitive ? '' : 'i'}`;
    const regex = new RegExp(escapeRegex(find), flags);
    const matches = (text.match(regex) || []).length;

    const result = text.replace(regex, (match) => {
      switch (transform.toLowerCase()) {
        case 'upper':
        case 'uppercase':
          return match.toUpperCase();
        case 'lower':
        case 'lowercase':
          return match.toLowerCase();
        case 'title':
        case 'capitalize':
          return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
        case 'toggle':
          return match.split('').map(char => 
            char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
          ).join('');
        default:
          return transform;
      }
    });

    return { text: result, matches };
  };

  const performNumberReplace = (text: string, find: string, replace: string, opts: ReplaceOptions) => {
    const numberRegex = /\b\d+(\.\d+)?\b/g;
    const matches = (text.match(numberRegex) || []).length;
    
    const result = text.replace(numberRegex, (match) => {
      if (find && !match.includes(find)) return match;
      return replace || match;
    });

    return { text: result, matches };
  };

  const performUrlReplace = (text: string, find: string, replace: string, opts: ReplaceOptions) => {
    const urlRegex = /https?:\/\/[^\s]+/g;
    const matches = (text.match(urlRegex) || []).length;
    
    const result = text.replace(urlRegex, (match) => {
      if (find && !match.includes(find)) return match;
      return replace || match;
    });

    return { text: result, matches };
  };

  const performEmailReplace = (text: string, find: string, replace: string, opts: ReplaceOptions) => {
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    const matches = (text.match(emailRegex) || []).length;
    
    const result = text.replace(emailRegex, (match) => {
      if (find && !match.includes(find)) return match;
      return replace || match;
    });

    return { text: result, matches };
  };

  const performDateReplace = (text: string, find: string, replace: string, opts: ReplaceOptions) => {
    const dateRegex = /\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/g;
    const matches = (text.match(dateRegex) || []).length;
    
    const result = text.replace(dateRegex, (match) => {
      if (find && !match.includes(find)) return match;
      return replace || match;
    });

    return { text: result, matches };
  };

  const performCustomPattern = (text: string, pattern: string, replace: string, opts: ReplaceOptions) => {
    try {
      const flags = `${opts.globalReplace ? 'g' : ''}${opts.caseSensitive ? '' : 'i'}${opts.multiline ? 'm' : ''}`;
      const regex = new RegExp(pattern, flags);
      const matches = (text.match(regex) || []).length;
      const result = text.replace(regex, replace);
      return { text: result, matches };
    } catch (error) {
      throw new Error(`Invalid custom pattern: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const escapeRegex = (str: string): string => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const replaceResult = useMemo(() => {
    return performReplace(input, findText, replaceText, selectedMethod, options);
  }, [input, findText, replaceText, selectedMethod, options]);

  useEffect(() => {
    setResult(replaceResult.result);
  }, [replaceResult]);

  const copyToClipboard = async () => {
    if (result) {
      await navigator.clipboard.writeText(result);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadResult = () => {
    if (result) {
      const summary = `Find & Replace Summary:
Method: ${replaceMethods.find(m => m.id === selectedMethod)?.name}
Find: "${findText}"
Replace: "${replaceText}"
Matches: ${replaceResult.stats.totalMatches}
Replacements: ${replaceResult.stats.replacements}
Lines Modified: ${replaceResult.stats.linesModified}
Characters Changed: ${replaceResult.stats.charactersChanged}

Result:
${result}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'find-replace-result.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleText = () => {
    const samples = [
      `Hello world! This is a test.
Hello everyone, welcome to our site.
Hello there, how are you?
This contains multiple hello words.
Replace all hello with hi.`,
      `Email: john@example.com
Phone: 555-123-4567
Date: 12/25/2023
Website: https://example.com
Contact: jane@company.org`,
      `TODO: Fix login bug
DONE: Update homepage
TODO: Add search feature
FIXME: Database connection
TODO: Write documentation`,
      `Version 1.0 released
Version 2.0 in progress
Version 3.0 planned
Upgrade to version 1.5
Download version 2.1`,
      `Apple, Banana, Cherry
APPLE, BANANA, CHERRY
apple, banana, cherry
Mixed Case Words Here
Convert To Lower Case`
    ];
    setInput(samples[Math.floor(Math.random() * samples.length)]);
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Replace className="text-orange-600" />
            Advanced Find & Replace
          </h1>
          <p className="text-gray-600">Powerful text search and replacement with multiple methods</p>
        </div>

        {/* Find & Replace Controls */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Search & Replace Controls</h2>
            <div className="flex gap-2">
              <button
                onClick={generateSampleText}
                className="flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Options
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Stats
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">Find Text</label>
              <input
                type="text"
                value={findText}
                onChange={(e) => setFindText(e.target.value)}
                placeholder="Enter text to find..."
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">Replace With</label>
              <input
                type="text"
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
                placeholder="Enter replacement text..."
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Status Messages */}
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={16} />
              <span className="text-red-700 text-sm">{errorMessage}</span>
            </div>
          )}

          {replaceResult.stats.totalMatches > 0 && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="text-green-600" size={16} />
              <span className="text-green-700 text-sm">
                Found {replaceResult.stats.totalMatches} match{replaceResult.stats.totalMatches !== 1 ? 'es' : ''}
                {replaceResult.stats.replacements > 0 && ` • Made ${replaceResult.stats.replacements} replacement${replaceResult.stats.replacements !== 1 ? 's' : ''}`}
              </span>
            </div>
          )}
        </div>

        {/* Options Panel */}
        {showOptions && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Replace Options</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Search Settings</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.caseSensitive}
                    onChange={(e) => setOptions({...options, caseSensitive: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Case Sensitive</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.wholeWords}
                    onChange={(e) => setOptions({...options, wholeWords: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Whole Words Only</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.useRegex}
                    onChange={(e) => setOptions({...options, useRegex: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Use Regular Expression</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Replace Settings</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.globalReplace}
                    onChange={(e) => setOptions({...options, globalReplace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Replace All Occurrences</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.multiline}
                    onChange={(e) => setOptions({...options, multiline: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Multiline Mode</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.preserveCase}
                    onChange={(e) => setOptions({...options, preserveCase: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Preserve Case</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Output Settings</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.trimResults}
                    onChange={(e) => setOptions({...options, trimResults: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Trim Line Whitespace</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Text Input and Output */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Original Text</label>
                <div className="text-sm text-gray-500">
                  {input.split('\n').length} lines
                </div>
              </div>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter your text here..."
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Result</label>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-500">
                    {result.split('\n').length} lines
                  </div>
                  <button
                    onClick={copyToClipboard}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadResult}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={result}
                readOnly
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none font-mono text-sm"
                placeholder="Result will appear here..."
              />
            </div>
          </div>
        </div>

        {/* Replace Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Search className="text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Replace Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {replaceMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>

          {/* Method Help */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">
              {replaceMethods.find(m => m.id === selectedMethod)?.name} Help
            </h3>
            <div className="text-sm text-gray-600">
              {selectedMethod === 'basic' && (
                <div>
                  <p>• Simple text replacement</p>
                  <p>• Use options panel for case sensitivity and other settings</p>
                </div>
              )}
              {selectedMethod === 'regex' && (
                <div>
                  <p>• Use regex patterns like <code className="bg-gray-200 px-1 rounded">\d+</code> for numbers</p>
                  <p>• Capture groups: <code className="bg-gray-200 px-1 rounded">(\w+)</code> and use <code className="bg-gray-200 px-1 rounded">$1</code> in replacement</p>
                </div>
              )}
              {selectedMethod === 'caseInsensitive' && (
                <div>
                  <p>• Ignores uppercase/lowercase differences</p>
                  <p>• "Hello" will match "hello", "HELLO", "Hello"</p>
                </div>
              )}
              {selectedMethod === 'wholeWord' && (
                <div>
                  <p>• Matches complete words only</p>
                  <p>• "cat" won't match "category" or "concatenate"</p>
                </div>
              )}
              {selectedMethod === 'lineReplace' && (
                <div>
                  <p>• Replaces entire lines containing the search term</p>
                  <p>• Useful for removing or replacing complete lines</p>
                </div>
              )}
              {selectedMethod === 'multiReplace' && (
                <div>
                  <p>• Enter multiple terms separated by commas</p>
                  <p>• Example: "cat, dog, bird" replaces all three with same text</p>
                </div>
              )}
              {selectedMethod === 'caseTransform' && (
                <div>
                  <p>• Transform case: "upper", "lower", "title", "toggle"</p>
                  <p>• Changes case of matched text instead of replacing content</p>
                </div>
              )}
              {selectedMethod === 'numberReplace' && (
                <div>
                  <p>• Finds and replaces numbers in text</p>
                  <p>• Leave find empty to replace all numbers</p>
                </div>
              )}
              {selectedMethod === 'urlReplace' && (
                <div>
                  <p>• Automatically detects and replaces URLs</p>
                  <p>• Matches http:// and https:// URLs</p>
                </div>
              )}
              {selectedMethod === 'emailReplace' && (
                <div>
                  <p>• Automatically detects and replaces email addresses</p>
                  <p>• Matches standard email format patterns</p>
                </div>
              )}
              {selectedMethod === 'dateReplace' && (
                <div>
                  <p>• Finds dates in MM/DD/YYYY or MM-DD-YYYY format</p>
                  <p>• Leave find empty to replace all dates</p>
                </div>
              )}
              {selectedMethod === 'customPattern' && (
                <div>
                  <p>• Advanced regex patterns with custom logic</p>
                  <p>• Use for complex pattern matching scenarios</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStats && input && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Replace Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Total Matches</h3>
                <div className="text-2xl font-bold text-blue-900">{replaceResult.stats.totalMatches}</div>
                <p className="text-sm text-blue-600">Found instances</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Replacements</h3>
                <div className="text-2xl font-bold text-green-900">{replaceResult.stats.replacements}</div>
                <p className="text-sm text-green-600">Text replaced</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Lines Modified</h3>
                <div className="text-2xl font-bold text-orange-900">{replaceResult.stats.linesModified}</div>
                <p className="text-sm text-orange-600">Changed lines</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Characters Changed</h3>
                <div className="text-2xl font-bold text-purple-900">{replaceResult.stats.charactersChanged}</div>
                <p className="text-sm text-purple-600">Net change</p>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Method Used</h4>
                <div className="text-sm text-gray-600">
                  {replaceMethods.find(m => m.id === selectedMethod)?.name}
                </div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Search Term</h4>
                <div className="text-sm text-gray-600 font-mono truncate">
                  {findText || 'None'}
                </div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Replacement</h4>
                <div className="text-sm text-gray-600 font-mono truncate">
                  {replaceText || 'None'}
                </div>
              </div>
            </div>

            {/* Options Summary */}
            <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <h4 className="font-semibold text-indigo-700 mb-2">Active Options</h4>
              <div className="flex flex-wrap gap-2">
                {options.caseSensitive && (
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">Case Sensitive</span>
                )}
                {options.wholeWords && (
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">Whole Words</span>
                )}
                {options.useRegex && (
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">Regex</span>
                )}
                {options.globalReplace && (
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">Global Replace</span>
                )}
                {options.multiline && (
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">Multiline</span>
                )}
                {options.preserveCase && (
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">Preserve Case</span>
                )}
                {options.trimResults && (
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">Trim Results</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FindReplaceComponent;